import { FC, useMemo } from 'react';
import { useRouter } from 'next/router';
import SearchHero from 'src/common/components/Search/SearchHero';
import { useSearchHeroVisibilityContext } from 'src/common/components/Search/SearchHeroVisibilityProvider';
import Button, { type ButtonProps } from 'src/common/components/ButtonDS22';
import Logo from 'src/common/components/Logo';
import Link from 'next/link';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import backNavigation from 'src/common/components/MobileWebViewHeader/backNavigation/backNavigation';
import { useRequestInfoContext } from 'src/common/providers/RequestInfoProvider';
import Typography from 'src/common/components/Typography';
import {
  isBookNow, isDLP, isHome, isLegal, isListing, isSearch,
} from 'src/common/components/MobileWebViewHeader/backNavigation/utils';
import styles from './MobileWebViewHeader.module.scss';
import { BackNavigationLocation } from './backNavigation/types';

type MobileWebViewHeaderProps = {
  catLocBreadcrumb?: BackNavigationLocation;
  /** Replaces the default backButton onClick handler. Use sparingly for edge cases. */
  onBackOverride?: () => void;
  title?: string;
};

/**
 * The MobileWebView header contains a back button that navigates using a combination of
 * simple `router.back` and a "hierarchy" back based on hierarchy of
 * `home > dlp > search > search with filters > listing > booking-inquiry`
 */
const MobileWebViewHeader: FC<MobileWebViewHeaderProps> = ({
  catLocBreadcrumb,
  onBackOverride,
  title,
}) => {
  const router = useRouter();
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const { heroInView } = useSearchHeroVisibilityContext(); // set by SearchHero or ListingHeroImage
  const requestInfo = useRequestInfoContext();

  /** WebView Header is expected to be show on these pages only */
  const page = useMemo(() => {
    if (isBookNow(router)) { return 'book-now'; }
    if (isListing(router)) { return 'trips'; }
    if (isHome(router)) { return 'home'; }
    if (isSearch(router)) { return 'search'; }
    if (isDLP(router)) { return 'dlp'; }
    if (isLegal(router)) { return 'legal'; }
    return null;
  }, [router]);
  /** Simpler than prop config, because heroInView unavailable from props */
  const show = useMemo(() => {
    if (page === 'home') {
      return {
        search: heroInView === false,
        logo: heroInView,
      };
    }
    if (page === 'dlp') {
      return {
        search: heroInView === false,
        logo: heroInView,
      };
    }
    if (page === 'book-now') {
      return {
        search: false,
        logo: false,
      };
    }
    if (page === 'trips') {
      return {
        search: false,
        logo: false,
      };
    }
    if (page === 'search') {
      return {
        search: true,
        logo: false,
      };
    }
    if (page === 'legal') {
      return {
        search: false,
        logo: true,
      };
    }
    return {};
  }, [heroInView, page]);

  const back = useMemo(() => backNavigation(router, requestInfo, catLocBreadcrumb), [
    catLocBreadcrumb,
    requestInfo,
    router,
  ]);

  type ButtonOrLink = Pick<ButtonProps, 'type' | 'onClick'> | Pick<ButtonProps, 'type' | 'href'> | null;

  const backButtonProps: ButtonOrLink = useMemo(() => {
    if (onBackOverride) {
      return {
        type: 'button',
        onClick: onBackOverride,
      };
    }
    if (back === 'routerBack') {
      return {
        type: 'button',
        onClick: router.back,
      };
    }
    if (back) {
      return {
        type: 'link', // link is used for prefetch
        href: back, // pushes one left in hierarchy
      };
    }
    return null;
  }, [back, onBackOverride, router.back]);

  if (!isMobileNativeWebView) return null;

  return (
    <div className={getClassNames(
      styles,
      'root',
      classNames(
        page,
        {
          tripsScroll: page === 'trips' && heroInView === true,
          fixed: page !== 'search' && page !== 'book-now',
        },
      ),
    )}
    >
      {page !== 'home' && backButtonProps && (
        <Button
          size="small"
          icon="caret-left"
          variant="primary-white"
          {...backButtonProps}
          fitWidth
        />
      )}
      {show.logo && (
        <div className={styles.logo}>
          <Link href="/">
            <Logo />
          </Link>
        </div>
      )}
      {show.search && (
        <SearchHero
          variant="navigation"
          theme={page === 'search' ? 'gray' : 'white'}
        />
      )}
      {title && (
        <div className={styles.title}>
          <Typography
            type="h2"
            weight="semibold"
            size="heading-5"
          >
            {title}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default MobileWebViewHeader;
